/**
  The definitions + functions in this file are all the pieces that make up the core of Types.
  The only pieces that should be in this file are pieces that need to access the internals of Type.
  For example: Functions that create new primitive Types, or access properties on primitive Types.
  Primitive types are _not_ the same as Builtin plugin types, such as Text or Record. Those are built
  from TypeConstructors and TypeApplications, which are primitives of the language, and are provided
  by the Builtin plugin. You can see what's in this file as representing the language itself, and the
  Builtin plugin types as the "standard library" of WFDL. In most cases, you shouldn't need to add,
  or change, much in this file. These pieces should, largely, be consumed outside of this file, to
  compose functions and types that make use of Types.

  All of these types + functions must live in one file, due to us enforcing Type as opaque. This means
  that Flow does not know what's inside of Type outside of this file. This provides us a more ergonomic
  developer experience across the rest of our codebase, as Flow errors will be more succinct to tell you
  that you did not use a Type properly, instead of listing every possible permutation of a Type that it could be.
 */

import {type Qualified, type TypeName} from '@packages/systems/wfdl-Qualified';

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
/**
  This type represents the union of all possible types. The basic structure of a type is:
  {
    type: string,
    val: any,
    system?: DataTypeSystem<any>,
    meta?: MetaAttributes}
  }
  You can find the definitions for these underlying types at the bottom of this file.
  The underlying definitions are at the bottom of this file, as you will not directly
  consume these types in any place, and as such, are of least importance when building
  real things when building with Types.
 */
export type Type =
  | ElementDataType
  | Generic
  | TypeReference
  | TypeConstructor
  | TypeApplication
  | RowEmpty
  | RowCons
  | UnknownType
  | RigidType;

/**
 * Get the meta property of a dataType.
 */
export const getMeta = (
  dataType: Type
): {
  [key: string]: any;
} | null => {
  if (typeof dataType.meta === 'object') {
    return dataType.meta;
  }

  return null;
};

/**
 * Get the system property of a dataType.
 */
export const getSystem = (dataType: Type): DataTypeSystem<any> | null => {
  if (typeof dataType.system === 'object') {
    return dataType.system;
  }

  return null;
};

/**
  Returns a new Type object, with the passed system object added.
 */
export const withSystem = (
  system: DataTypeSystem<any>,
  dataType: Type
): Type => ({
  ...dataType,
  system,
});

// This works by designating fields to be "empty" by name. When a field is empty, this essentially forbids using fields with certain names. The `MetaAttributes` type now technically has the names of "withSystem" fields but with a different value
// ```
// bindUIVariant: empty
// ```
type NeverOverlapWithDataTypeSystem = {
  [key in keyof DataTypeSystem<any>]: never;
};

/**
 * Meta attributes cannot be "withSystem" attributes.
 * Please rename your attribute to something other than keys of DataTypeSystem.
 *
 *
 */
export type MetaAttributes = NeverOverlapWithDataTypeSystem & {
  [key: string]: any;
};

/**
  Returns a new Type object, with the passed meta object added.
 */
export const withMeta = (meta: MetaAttributes, dataType: Type): Type => ({
  ...dataType,
  meta,
});

export const TGeneric = ({arg, body}: {arg: string; body: Type}): Type => ({
  type: 'Generic',
  val: {
    arg,
    body,
  },
});

export const TTypeReference = (val: string): Type => ({
  type: 'TypeReference',
  val,
});

export const TTypeApplication = (val: {con: Type; arg: Type}): Type => ({
  type: 'TypeApplication',
  val,
});

export const TTypeConstructor = (val: Qualified<TypeName>): Type => ({
  type: 'TypeConstructor',
  val,
});

export const TRowEmpty: Type = {type: 'RowEmpty'};

export const TRowCons = (val: {
  label: string;
  type: Type;
  tail: Type;
}): Type => ({type: 'RowCons', val});

export const TElement: Type = {type: 'Element'};

export const TUnknown = (val: {
  id: UnknownId;
  rigids: Partial<Record<RigidId, true>>;
}): Type => ({type: 'Unknown', val});

export const TRigid = (val: RigidId): Type => ({type: 'Rigid', val});

/**
 * Providing a dataType with a type of `Element` indicates to core that this is *not* a leaf node. If this `dataType` is missing a leaf node is assumed automatically constrains elements from being placed in this Atom.
 *
 * ```javascript
 * const MyAtom = {
 *  dataType: {type: 'Record', fields: {children: {type: 'List', of: {type: 'Element'}}}},
 * };
 * ```
 */
// This type is not exact in Flow terms because MetaAttributes adds extra fields like `bindUIVariant: empty`
// in order to forbid using "withSystem" types in "withMeta" calls.
// When the type is exact, it's forbidden to add the extra fields we needed.
type TextMeta = NeverOverlapWithDataTypeSystem & {
  // Value used as a placeholder.
  placeholder?: string;
  // Indicates that the UI should allow multiple lines of text.
  multiLine?: boolean;
  validation?: {
    regex: string;
  };
  [key: string]: any;
};

export type ElementDataType = {
  type: 'Element';
  meta?: TextMeta;
  system?: DataTypeSystem<Array<any>>;
};

/**
 * The shape of `Generic` with `arg` and `body` is similar to the shape of
 * the `Function` expression. This is on purpose. A `Generic` is very much like
 * a function but at the type level; with the compiler in charge of implicitly
 * “calling” it with an appropriate value (type).
 */
export type Generic = {
  type: 'Generic';
  val: {
    arg: string;
    body: Type;
  };
  system?: DataTypeSystem<any>;
  meta?: MetaAttributes;
};

/**
 * A `TypeReference` is used to refer to a type variable previously introduced in
 * scope by a `Generic`.
 */
export type TypeReference = {
  type: 'TypeReference';
  val: string;
  system?: DataTypeSystem<any>;
  meta?: MetaAttributes;
};

/**
 * A `TypeConstructor` is a reference to a type introduced by a plugin.
 * It is a namespaced replacement for the `Plugin` type.
 */
export type TypeConstructor = {
  type: 'TypeConstructor';
  val: Qualified<TypeName>;
  system?: DataTypeSystem<any>;
  meta?: MetaAttributes;
};

/**
 * Used to apply type constructors with arity > 0 to type arguments in order
 * to produce concrete types.
 */
export type TypeApplication = {
  type: 'TypeApplication';
  val: {
    con: Type;
    arg: Type;
  };
  system?: DataTypeSystem<any>;
  meta?: MetaAttributes;
};

export type RowEmpty = {
  type: 'RowEmpty';
  // The below are unused and defined only for Flow
  system?: DataTypeSystem<any>;
  meta?: MetaAttributes;
};

export type RowCons = {
  type: 'RowCons';
  val: {
    label: string;
    type: Type;
    tail: Type;
  };
  system?: DataTypeSystem<any>;
  meta?: MetaAttributes;
};

export type UnknownId = number;
export type RigidId = number;

/**
 * Also known as a unification variable, an unknown type is only used as a
 * placeholder for yet unknown types during type checking and inference.
 */
export type UnknownType = {
  type: 'Unknown';
  val: {
    id: UnknownId;
    // Rigid types in scope for this unknown type. These are used to make sure
    // we don’t unify unknowns with rigid types that is not in scope.
    rigids: Partial<Record<RigidId, true>>;
  };
  // The below are unused and defined only for Flow
  system?: DataTypeSystem<any>;
  meta?: MetaAttributes;
};

/**
 * Also known as a skolem constant, a rigid type is a type used during type
 * checking and inference. A rigid type replaces type references when checking
 * inside generic types and only unifies with itself and unknowns.
 */
export type RigidType = {
  type: 'Rigid';
  val: RigidId;
  // The below are unused and defined only for Flow
  system?: DataTypeSystem<any>;
  meta?: MetaAttributes;
};

export type DeconstructedRow = {
  items: {
    [key: string]: Type;
  };
  tail: Type;
};

/**
 * Properties that live on the `dataType.system` of every `Type`.
 *
 * If key names change, please update the systemKeys set in this file.
 */
export type DataTypeSystem<ValueT> = {
  // Primary UI description for the input.
  label?: string;
  /**
   * If true:
   * - Hide UI Settings (ExpresisonEditor) for this Type.
   * TODO: rename to hiddenSettings to match with hiddenProperty
   */
  private?: boolean;
  /**
   * If true:
   *  - Hide from the binding menu, including any sub-properties
   *  - Hide from the Props panel
   */
  hiddenProperty?: boolean;
  // Flag this parameter as "unlinked" - used only by component instances for now.
  isUnlinked?: boolean;
  // Flag indicating this parameter is using the default value - used by component instances
  isDefault?: boolean;
  // If true, the label takes the entire width (eg so it sits above the settings UI instead of the left)
  isFullWidth?: boolean;
  // The "human-readable" text used to refer to this type anywhere in the UI.
  displayName?: string;
  /** Should the label be hidden or emphasized (bolded)? */
  labelVariant?: 'hidden' | 'emphasized';
  /**
   * The width of the label in the UI.
   * `narrow` and `narrower` will adjust the font-size and letter-spacing
   * in an attempt to fit the text within the available space.
   */
  labelWidth?: 'narrow' | 'narrowest';
  defaultValue?: ValueT;
  // Boolean that determines if a property can be bound
  isBindable?: boolean;
  // Choose one of the custom binding UIs
  bindUIVariant?: 'custom';
  // Boolean that determines if a dataType should have a divider/separator line below it
  divider?: boolean;
  /**
   * The group under which the Type should be rendered in the binding UI.
   */
  group?: {
    /**
     * The key for the binding option in the binding UI under which the types
     * belonging to the same group are rendered.
     *
     * `recordKey` should correspond to a field on the data type. If the field
     * exists and is a matching binding option, it will be a selectable option
     * in the binding UI with the subfields (i.e. the types with the same
     * `group.recordKey`) grouped under it.
     *
     * If the field exists on the data type but isn't a matching binding option,
     * it will be rendered as a virtual record with the subfields grouped under
     * it.
     *
     * Basically this functionality allows you to group fields under one of
     * their siblings.
     *
     * For example, if the fields `friend.name` and `friend.age` both have
     * `group: {recordKey: 'friend'}` and the field
     * `friend` exists on the data type, the binding UI would render the
     * binding option for `friend` instead of a virtual record and group
     * `friend.name` and `friend.age` under it.
     *
     * - friend <- this is the binding option
     *   - friend.name
     *   - friend.age
     */
    recordKey: string;
  };
  // Optional hint text above a field, only shown if
  // the label is set to fullWidth
  hintText?: string;
  // Text for an optional tooltip next to label, only shown if
  // the label is set to fullWidth
  toolTipText?: string;
  /** Whether this type can be externalized via a localization binding. */
  isLocalizable?: boolean;
};

/**
 * Used by tooling to dynamically detect if an object is an Type Constructor.
 */
function isTypeConstructor(obj: any): boolean {
  // The flow magic here is to win Flow's help in keeping this function
  // up to date using exhaustive checking by casting to empty.
  const type: Type = obj as Type;

  switch (type.type) {
    case 'Rigid':
    case 'Generic':
    case 'Element':
    case 'RowCons':
    case 'RowEmpty':
    case 'TypeApplication':
    case 'TypeConstructor':
    case 'TypeReference':
    case 'Unknown':
      return true;

    default:
      /*:: (type: empty); */
      return false;
  }
}

const VALID_TYPE_KEYS = new Set(['val', 'type', 'meta', 'system']);
function isValidTypeKey(key: any) {
  return VALID_TYPE_KEYS.has(key);
}

function isType(obj: any): boolean {
  if (typeof obj === 'object' && !Array.isArray(obj) && obj !== null) {
    return isTypeConstructor(obj) && Object.keys(obj).every(isValidTypeKey);
  }
  return false;
}

export function getTypeFromAny(obj: any): Type | null {
  if (!isType(obj)) return null;
  return obj as Type;
}

/**
 * The keys from DataTypeSystem, used at runtime during parsing of wfdlType
 */
export const systemKeys = new Set<string>([
  'label',
  'private',
  'isUnlinked',
  'isDefault',
  'isFullWidth',
  'displayName',
  'labelVariant',
  'defaultValue',
  'isBindable',
  'bindUIVariant',
  'divider',
  'group',
  'hintText',
  'toolTipText',
  'isLocalizable',
]);
